import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2e309ef6"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "claiming__header" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", { class: "claiming__title" }, "Claiming", -1))
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "claiming__sub-title" }, "Select your Polkadot account", -1))
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "claiming__sub-title" }, "Sign message with your Ethereum address", -1))
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h4", { class: "claiming__third-title" }, " 1. Copy the following string and sign it with the Ethereum account you used during the pre-sale: ", -1))
const _hoisted_6 = { class: "claiming__textarea" }
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("br", null, null, -1))
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h4", { class: "claiming__third-title" }, " 2. Paste the signed message into the field below: ", -1))

import WhiteWrapper from "@/components/white-wrapper/index.vue";
import ButtonsBlock from "@/components/buttons-block/index.vue";
import BaseButton from "@/components/base-button/index.vue";
import BackButton from "@/components/back-button/index.vue";
import SelectAccountInput from "@/components/select-account-input/index.vue";
import CopyIcon from "@/icons/common/copy-icon.vue";
import ClaimAddressInput from "../claim/components/claim-address-input.vue";
import ClaimingAmount from "./components/claiming-amount.vue";
import ClaimingError from "./components/claiming-error.vue";
import ClaimingSuccess from "./components/claiming-success.vue";
import { Account } from "@/types/account";
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  accounts,
  apiPromise,
  nativeToken,
  selectedNetwork,
  signer,
  walletSelected,
} from "@/stores";
import { isAddress } from "web3-utils";
import createIcon from "@/libs/identicon/ethereum";
import type { Option } from "@polkadot/types";
import type { BalanceOf, StatementKind } from "@polkadot/types/interfaces";
import BigNumber from "bignumber.js";
import { fromBase } from "@/utils/units";
import { BN_ZERO } from "@polkadot/util";
import { decodeAddress } from "@polkadot/util-crypto";
import { Buffer } from "buffer";
import { Network } from "@/types/network";
import ClaimingPreclaim from "./components/claiming-preclaim.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const router = useRouter();
const route = useRoute();

const myAccount = ref<Account>();
const isMyAccountLoading = ref<boolean>(false);
const preclaimedAddress = ref<string>();
const signedMessage = ref<string>("");
const isSend = ref<boolean>(false);
const isError = ref<boolean>(false);

const ethAccount = ref<Account>();
const ethAddress = ref<string>("");
const isValid = ref<boolean>(false);
const isEthLoading = ref<boolean>(false);
const claimable = ref<BigNumber>(new BigNumber(0));

onMounted(() => {
  const trimmed = route.query.ethAddress?.toString().trim() || "";
  ethAddress.value = trimmed;
  const isValidAddress = isAddress(trimmed);

  if (!isValidAddress || walletSelected.value.id === 1) {
    router.push({ name: "claim" });
    return;
  }

  addressInput(trimmed);
});

watch(myAccount, async () => {
  if (myAccount.value) {
    try {
      isMyAccountLoading.value = true;
      const api = await apiPromise.value;
      // Get preclaim address info
      if (api.query.claims.preclaims) {
        const preclaims = await api.query.claims.preclaims<Option<BalanceOf>>(
          myAccount.value.address
        );
        const result = preclaims.unwrapOr(null);
        preclaimedAddress.value = result ? result.toString() : undefined;
      }

      isMyAccountLoading.value = false;
    } catch (err) {
      console.error(
        "Unexpected error when querying if account has preclaims",
        err
      );
      isMyAccountLoading.value = false;
    }
  }
});

watch(ethAccount, async () => {
  if (ethAccount.value) {
    try {
      isEthLoading.value = true;
      const api = await apiPromise.value;

      // Get claimable amount info
      const claims = await api.query.claims.claims<Option<BalanceOf>>(
        ethAccount.value.address
      );
      claimable.value = new BigNumber(
        fromBase(
          claims.unwrapOr(BN_ZERO).toString(),
          nativeToken.value.decimals
        )
      );
      isValid.value = claimable.value.gt(0);

      isEthLoading.value = false;
    } catch (err) {
      console.error(
        "Unexpected error when querying if account has claimable balance",
        err
      );
      isEthLoading.value = false;
    }
  }
});

const nextAction = async () => {
  if (!myAccount.value || !ethAccount.value) {
    return;
  }

  try {
    isMyAccountLoading.value = true;
    isError.value = false;
    const api = await apiPromise.value;
    const statement =
      "I hereby agree to the terms of the statement whose SHA-256 multihash is Qmc1XYqT6S39WNp2UeiRUrZichUWUPpGEThDE6dAb3f6Ny. (This may be found at the URL: https://statement.polkadot.network/regular.html)";

    if (preclaimedAddress.value) {
      const unsubscribe = await api.tx.claims.attest(statement).signAndSend(
        myAccount.value.address,
        {
          signer: signer.value,
          nonce: -1,
        },
        async (result) => {
          if (!result || !result.status) {
            return;
          }

          if (result.status.isFinalized || result.status.isInBlock) {
            result.events
              .filter(({ event: { section } }) => section === "system")
              .forEach(({ event: { method } }): void => {
                if (method === "ExtrinsicFailed") {
                  // Handle error
                } else if (method === "ExtrinsicSuccess") {
                  // Handle succes
                }
              });
          } else if (result.isError) {
            // Handle error
          }

          if (result.isCompleted) {
            unsubscribe();
          }
        }
      );
    } else {
      // Check if it is claim only or claim and attest
      const typeRes = await api.query.claims.signing<Option<StatementKind>>(
        ethAccount.value.address
      );
      const claimOnly = !typeRes.unwrapOr(null) || !api.tx.claims.claimAttest;
      const transaction = claimOnly
        ? api.tx.claims.claim(myAccount.value.address, signature.value)
        : api.tx.claims.claimAttest(
            myAccount.value.address,
            signature.value,
            statement
          );

      const unsubscribe = await transaction.send(async (result) => {
        if (!result || !result.status) {
          return;
        }

        if (result.status.isFinalized || result.status.isInBlock) {
          result.events
            .filter(({ event: { section } }) => section === "system")
            .forEach(({ event: { method } }): void => {
              if (method === "ExtrinsicFailed") {
                // Handle error
              } else if (method === "ExtrinsicSuccess") {
                // Handle succes
              }
            });
        } else if (result.isError) {
          // Handle error
        }

        if (result.isCompleted) {
          unsubscribe();
        }
      });
    }

    isMyAccountLoading.value = false;
    isSend.value = true;
  } catch (err) {
    console.error("Unexpected error when trying to claim", err);
    isMyAccountLoading.value = false;
    isError.value = true;
  }
};

const back = () => {
  router.go(-1);
};

const selectMyAccount = (account: Account) => {
  myAccount.value = account;
};

const addressInput = (newVal: string) => {
  const trimmed = newVal.trim();
  ethAddress.value = trimmed;
  const isValidAddress = isAddress(trimmed);
  ethAccount.value = isValidAddress
    ? {
        id: Number.MAX_SAFE_INTEGER,
        name: "",
        image: createIcon(trimmed),
        address: trimmed,
        isLedger: false,
      }
    : undefined;
};

const copy = () => {
  navigator.clipboard.writeText(messagePart1.value + messagePart2.value);
};

const buttonTitle = computed(() => {
  let title = "Claim ";
  title += claimable.value;
  title += " " + nativeToken.value.symbol.toLocaleUpperCase();

  return title;
});

const messagePart1 = computed(
  () =>
    `Pay ${nativeToken.value.symbol.toLocaleUpperCase()}s to the ${
      Network[selectedNetwork.value]
    } account:`
);

const messagePart2 = computed(() => {
  if (!myAccount.value?.address) {
    return "";
  }

  const decoded = decodeAddress(myAccount.value.address);
  const stringFormatted = Buffer.from(decoded).toString("hex");
  return stringFormatted;
});
const signature = computed(() => {
  try {
    const { sig } = JSON.parse(signedMessage.value || "");

    return sig;
  } catch (err) {
    console.warn("Could not extract signature from signed message", err);
    return undefined;
  }
});

return (_ctx: any,_cache: any) => {
  return (!isSend.value)
    ? (_openBlock(), _createBlock(WhiteWrapper, {
        key: 0,
        class: "claiming__wrap"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(BackButton, { action: back })
          ]),
          _hoisted_2,
          _createVNode(ClaimingAmount, {
            token: _unref(nativeToken),
            amount: claimable.value.toNumber()
          }, null, 8, ["token", "amount"]),
          _hoisted_3,
          _createVNode(SelectAccountInput, {
            account: myAccount.value,
            accounts: _unref(accounts),
            amount: claimable.value.toNumber(),
            "is-amount": true,
            token: _unref(nativeToken),
            title: "Claim to account",
            "onUpdate:select": selectMyAccount
          }, null, 8, ["account", "accounts", "amount", "token"]),
          (preclaimedAddress.value)
            ? (_openBlock(), _createBlock(ClaimingPreclaim, {
                key: 0,
                "preclaim-address": preclaimedAddress.value
              }, null, 8, ["preclaim-address"]))
            : _createCommentVNode("", true),
          _hoisted_4,
          _createVNode(ClaimAddressInput, {
            account: ethAccount.value,
            value: ethAddress.value,
            "onUpdate:address": addressInput
          }, null, 8, ["account", "value"]),
          _hoisted_5,
          _createElementVNode("div", _hoisted_6, [
            _createTextVNode(_toDisplayString(messagePart1.value), 1),
            _hoisted_7,
            _createTextVNode(_toDisplayString(messagePart2.value) + " ", 1),
            _createElementVNode("a", {
              class: "claiming__copy",
              onClick: copy
            }, [
              _createVNode(CopyIcon)
            ])
          ]),
          _hoisted_8,
          _withDirectives(_createElementVNode("textarea", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((signedMessage).value = $event)),
            class: "claiming__textarea claiming__textarea--paste",
            placeholder: "Paste here"
          }, null, 512), [
            [_vModelText, signedMessage.value]
          ]),
          (isError.value)
            ? (_openBlock(), _createBlock(ClaimingError, { key: 1 }))
            : _createCommentVNode("", true),
          _createVNode(ButtonsBlock, null, {
            default: _withCtx(() => [
              _createVNode(BaseButton, {
                title: buttonTitle.value,
                action: nextAction,
                send: true,
                disabled: isEthLoading.value || isMyAccountLoading.value || !signature.value || !isValid.value
              }, null, 8, ["title", "disabled"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }))
    : (_openBlock(), _createBlock(WhiteWrapper, { key: 1 }, {
        default: _withCtx(() => [
          _createVNode(ClaimingSuccess, {
            token: _unref(nativeToken),
            amount: claimable.value.toNumber()
          }, null, 8, ["token", "amount"])
        ]),
        _: 1
      }))
}
}

})